/**
 * Analytics Consent — Single source of truth.
 *
 * ARCHITECTURE:
 * Consent is stored in a first-party cookie (`virza.consent`) readable
 * immediately on page load — no API call, no hydration delay. This is the
 * client-authoritative model used by Stripe, Notion, and Linear.
 *
 * Three states:
 *   - absent  → unknown (never asked, no tracking)
 *   - 'accepted' → user opted in, PostHog may initialize
 *   - 'declined' → user opted out, no tracking
 *
 * GDPR compliance: the default is NO tracking. PostHog must not initialize,
 * set cookies, or make network calls until consent === 'accepted'.
 *
 * Server sync: after login, the consent banner reads the server-stored
 * preference and reconciles with the cookie. The most-recent decision wins.
 * This ensures cross-device consistency for authenticated users while
 * keeping the cookie as the fast-path source of truth on page load.
 *
 * LIFECYCLE:
 *   1. Page loads → read cookie → gate PostHog
 *   2. Banner shown if cookie absent (or declined + 90-day re-ask)
 *   3. User clicks Accept → write cookie + notify listeners + init PostHog
 *   4. User clicks Decline → write cookie + notify listeners + teardown PostHog
 *   5. On login → server sync reconciles cookie ↔ server preference
 */

'use client';

import { logInfo } from '@/lib/client-logger';

// ─── Cookie Management ──────────────────────────────────────────────────

const CONSENT_COOKIE = 'virza.consent';
const COOKIE_MAX_AGE_DAYS = 365;

export type ConsentState = 'accepted' | 'declined' | 'unknown';

/** Read consent from first-party cookie. Returns 'unknown' if absent or during SSR. */
export function readConsent(): ConsentState {
  if (typeof document === 'undefined') return 'unknown';
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]*)`));
    const value = match?.[1];
    if (value === 'accepted' || value === 'declined') return value;
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/** Write consent to a first-party cookie with SameSite=Lax, 1-year expiry. */
export function writeConsent(state: 'accepted' | 'declined'): void {
  if (typeof document === 'undefined') return;
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${CONSENT_COOKIE}=${state};path=/;max-age=${maxAge};SameSite=Lax`;
  logInfo('consent.state_changed', { state });
  notifyListeners(state);
}

/** Clear the consent cookie (e.g. on sign-out to reset for next user). */
export function clearConsent(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${CONSENT_COOKIE}=;path=/;max-age=0;SameSite=Lax`;
}

// ─── Reactive Listeners ─────────────────────────────────────────────────
// PostHogProvider subscribes to consent changes so it can initialize/teardown
// reactively without polling or re-rendering the provider tree.

type ConsentListener = (state: ConsentState) => void;
const listeners = new Set<ConsentListener>();

export function onConsentChange(listener: ConsentListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyListeners(state: ConsentState): void {
  listeners.forEach((fn) => fn(state));
}

// ─── Convenience ────────────────────────────────────────────────────────

/** Returns true ONLY if the user has explicitly accepted. Unknown = false. */
export function hasAnalyticsConsent(): boolean {
  return readConsent() === 'accepted';
}
